import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CardBatch,
  cardHistoryQueryOptions,
  cardJobsQueryOptions,
  type CardJobSummary,
  isOwnBatch,
  useBatchLiveProgress,
  useBatchProgress,
} from '../../api/cards';
import { Modal } from './Modal';

/**
 * A batch is "running" iff the backend says so. `status` is computed server-side
 * from a durable `completedAt` marker, so the client never infers lifecycle from
 * timestamps or cache presence.
 */
function isRunning(b: CardBatch): boolean {
  return b.status === 'processing';
}

/**
 * A pre-flight batch worth tracking: resolving, or finished and waiting on the
 * user's review. CardBatch rows only exist from commit onwards, so these come
 * from /cards/jobs instead — without them the whole resolve phase is invisible.
 */
function isActivePreflight(j: CardJobSummary): boolean {
  return (
    j.kind === 'resolve' && (j.status === 'processing' || j.status === 'review')
  );
}

export function JobsTracker() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Poll history; refetch fast only when running > 0
  const { data: history } = useQuery({
    ...cardHistoryQueryOptions(),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) {
        return 30_000;
      }
      const running = data.filter(isRunning).length;
      return running > 0 ? 4_000 : 30_000;
    },
  });

  // Same poll-fast-while-active rhythm as the history query above.
  const { data: jobs } = useQuery({
    ...cardJobsQueryOptions(),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) {
        return 30_000;
      }
      return data.some(isActivePreflight) ? 4_000 : 30_000;
    },
  });

  const running = useMemo(() => (history ?? []).filter(isRunning), [history]);
  const preflights = useMemo(
    () => (jobs ?? []).filter(isActivePreflight),
    [jobs],
  );

  // Collapse the tracker the moment the queue drains, so it reopens closed
  // next time a job appears (set-during-render on the empty transition).
  const isEmpty = running.length === 0 && preflights.length === 0;
  const [wasEmpty, setWasEmpty] = useState(isEmpty);
  if (isEmpty !== wasEmpty) {
    setWasEmpty(isEmpty);
    if (isEmpty) {
      setOpen(false);
    }
  }

  if (isEmpty) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed right-6 bottom-6 z-40 flex items-center gap-3 border border-ink bg-ink px-4 py-3 text-inverse-fg shadow-[4px_4px_0_var(--ink)] transition-transform hover:-translate-y-0.5"
        title={t('jobs.indicatorTitle', 'Jobs en cours')}
      >
        <span className="display text-lg text-green">⟳</span>
        <span className="text-sm font-medium">
          {t('jobs.indicatorCount', {
            count: running.length + preflights.length,
            defaultValue: `${running.length + preflights.length} jobs en cours`,
          })}
        </span>
      </button>

      {open && (
        <Modal
          aria-label={t('jobs.drawerLabel', 'Jobs en cours')}
          onClose={() => setOpen(false)}
          className="backdrop:bg-muted-soft"
        >
          <aside className="absolute top-0 right-0 size-full max-w-sm overflow-y-auto border-l-2 border-l-ink bg-card text-ink">
            <header className="flex items-center justify-between border-b-2 border-b-ink px-5 py-4">
              <div>
                <p className="label dash">
                  {t('jobs.drawerSubtitle', 'EN COURS')}
                </p>
                <h2 className="display mt-1 text-2xl">
                  {t('jobs.drawerTitle', 'JOBS')}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mono border border-rule px-2 py-1 text-xs"
                aria-label={t('common.close', 'Fermer')}
              >
                ESC
              </button>
            </header>

            <ul>
              {preflights.map((job) => (
                <PreflightRow
                  key={job.jobId}
                  job={job}
                  onNavigate={() => setOpen(false)}
                />
              ))}
              {running.map((batch) => (
                <BatchRow
                  key={batch.id}
                  batch={batch}
                  onNavigate={() => setOpen(false)}
                />
              ))}
            </ul>

            <footer className="border-t border-t-rule px-5 py-3">
              <Link
                to="/history"
                onClick={() => setOpen(false)}
                className="btn-link leading-(--text-xs--line-height)"
              >
                {t('jobs.viewAll', 'Voir tout l’historique')} →
              </Link>
            </footer>
          </aside>
        </Modal>
      )}
    </>
  );
}

/**
 * A pre-flight batch: live progress while it resolves, an "awaiting review"
 * badge once the recap is ready. Both states link back into the generator via
 * /cards?resume=<jobId>, which re-enters the stream or the recap.
 */
function PreflightRow({
  job,
  onNavigate,
}: {
  job: CardJobSummary;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const resolving = job.status === 'processing';
  // Only stream progress while resolving — a review row is static.
  const { data: progress } = useBatchProgress(resolving ? job.jobId : null);

  const done = progress?.done ?? job.done;
  const total = progress?.total ?? job.total;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <li className="border-b border-b-rule">
      <Link
        to="/cards"
        search={{ resume: job.jobId }}
        onClick={onNavigate}
        className="block px-5 py-4 transition-colors hover:bg-(--bg-soft)"
      >
        <div className="mb-2 flex items-center gap-3">
          <span
            className="display text-base"
            style={{ color: resolving ? 'var(--green)' : 'var(--blue)' }}
          >
            {resolving ? '⟳' : '✓'}
          </span>
          <span className="flex-1 truncate text-sm font-medium">
            {t('jobs.preflight', 'Pré-vol')}
          </span>
          <span
            className="mono px-1.5 py-0.5 text-[10px]"
            style={{
              background: resolving ? 'var(--green)' : 'var(--blue)',
              color: resolving ? 'var(--ink)' : 'var(--inverse-fg)',
            }}
          >
            {resolving
              ? t('jobs.processing', 'En cours')
              : t('jobs.awaitingReview', 'À valider')}
          </span>
        </div>

        {resolving && (
          <div
            className="relative mb-2 h-1 bg-rule"
            aria-label={`${done}/${total}`}
          >
            <div
              className="absolute inset-y-0 left-0 bg-green transition-[width] duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        <p className="mono text-xs text-muted">
          {done}/{total} ·{' '}
          {t('jobs.rowCount', {
            count: total,
            defaultValue: `${total} lignes`,
          })}{' '}
          · {new Date(job.createdAt).toLocaleTimeString()}
        </p>
      </Link>
    </li>
  );
}

function BatchRow({
  batch,
  onNavigate,
}: {
  batch: CardBatch;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const { data: progress } = useBatchLiveProgress(batch);

  const done = progress?.done ?? 0;
  const total = progress?.total ?? batch.rowCount;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  // Own batch → back into the generator's run & download step (live stream +
  // download). Someone else's (admins see every running batch here) → the
  // history detail: only the owner can attach to the progress stream.
  const mine = isOwnBatch(batch);
  const linkProps = mine
    ? ({ to: '/cards', search: { job: batch.jobId } } as const)
    : ({ to: '/history/$batchId', params: { batchId: batch.id } } as const);

  return (
    <li className="border-b border-b-rule">
      <Link
        {...linkProps}
        onClick={onNavigate}
        className="block px-5 py-4 transition-colors hover:bg-(--bg-soft)"
      >
        <div className="mb-2 flex items-center gap-3">
          <span className="display text-base text-green">⟳</span>
          <span className="flex-1 truncate text-sm font-medium">
            {batch.templateName}
          </span>
          <span className="mono bg-green px-1.5 py-0.5 text-[10px] text-ink">
            {batch.outputFormat.toUpperCase()}
          </span>
        </div>

        {/* Progress bar: live over SSE for one's own batch, polled stats otherwise */}
        <div
          className="relative mb-2 h-1 bg-rule"
          aria-label={`${done}/${total}`}
        >
          <div
            className="absolute inset-y-0 left-0 bg-green transition-[width] duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>

        <p className="mono text-xs text-muted">
          {done}/{total} ·{' '}
          {t('jobs.rowCount', {
            count: total,
            defaultValue: `${total} lignes`,
          })}{' '}
          · {new Date(batch.createdAt).toLocaleTimeString()}
        </p>
      </Link>
    </li>
  );
}
